import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { BASE_URL } from '../api';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Filter,
  RefreshCw,
  Tag,
  Layers,
  ToggleLeft,
  ToggleRight,
  Box,
  Archive,
  ClipboardList,
  PlusCircle,       // NEW — Add Stock icon
  TrendingUp,       // NEW — stock indicator
  BarChart2,        // NEW — stats
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE = BASE_URL;
const ENDPOINT = '/api/inventory/inventory-items/';
const PAGE_SIZE = 10;

const CATEGORY_OPTIONS = ['CONSUMABLE', 'CAPITAL'];
const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const CATEGORY_META = {
  CONSUMABLE: { color: 'bg-violet-100 text-violet-700 border border-violet-200', icon: Box },
  CAPITAL: { color: 'bg-amber-100 text-amber-700 border border-amber-200', icon: Archive },
};

const STATUS_META = {
  ACTIVE: { color: 'bg-emerald-100 text-emerald-700 border border-emerald-200', dot: 'bg-emerald-500' },
  INACTIVE: { color: 'bg-rose-100 text-rose-700 border border-rose-200', dot: 'bg-rose-400' },
};

const EMPTY_FORM = { item_name: '', item_code: '', category: 'CONSUMABLE', unit: '', status: 'ACTIVE' };

// ─── Toast Component ──────────────────────────────────────────────────────────

const Toast = ({ toasts, removeToast }) => (
  <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => (
      <div
        key={t.id}
        className={`
          pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl text-sm font-medium
          min-w-[280px] max-w-[360px] border backdrop-blur-sm
          animate-[slideIn_0.25s_ease_forwards]
          ${t.type === 'success' ? 'bg-white border-emerald-200 text-emerald-800' : ''}
          ${t.type === 'error' ? 'bg-white border-rose-200 text-rose-800' : ''}
          ${t.type === 'info' ? 'bg-white border-blue-200 text-blue-800' : ''}
        `}
      >
        {t.type === 'success' && <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500 mt-0.5" />}
        {t.type === 'error' && <XCircle className="h-5 w-5 shrink-0 text-rose-500 mt-0.5" />}
        {t.type === 'info' && <Loader2 className="h-5 w-5 shrink-0 text-blue-500 mt-0.5 animate-spin" />}
        <span className="flex-1 leading-snug">{t.message}</span>
        <button onClick={() => removeToast(t.id)} className="opacity-50 hover:opacity-100 transition-opacity">
          <X className="h-4 w-4" />
        </button>
      </div>
    ))}
  </div>
);

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

const ConfirmDialog = ({ open, item, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6 animate-[fadeUp_0.2s_ease_forwards]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-rose-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">Delete Inventory Item</h3>
            <p className="mt-1 text-sm text-slate-500 leading-relaxed">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-slate-700">"{item?.item_name}"</span>? This action
              will mark the item as inactive and cannot be easily undone.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete Item
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Add Stock Modal ──────────────────────────────────────────────────────────

const AddStockModal = ({ open, item, onClose, onSuccess }) => {
  const [quantity, setQuantity] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setQuantity('');
      setRemarks('');
      setError('');
    }
  }, [open]);

  if (!open || !item) return null;

  const handleSubmit = async () => {
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) {
      setError('Please enter a valid quantity greater than 0.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        `${API_BASE}${ENDPOINT}${item.id}/add-stock/`,
        { quantity: qty, remarks: remarks.trim() }
      );
      onSuccess(data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.quantity?.[0] ||
        'Failed to add stock. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[995] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md animate-[fadeUp_0.2s_ease_forwards] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Add Stock</h2>
              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[220px]">
                {item.item_name} · {item.item_code}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Current Stock Summary */}
        <div className="px-6 pt-5">
          <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">Assigned</p>
              <p className="text-lg font-bold text-slate-800">
                {item.total_assigned ?? 0}
              </p>
              <p className="text-xs text-slate-400">{item.unit}</p>
            </div>
            <div className="text-center border-x border-slate-200">
              <p className="text-xs text-slate-500 mb-1">Used</p>
              <p className="text-lg font-bold text-rose-600">
                {item.total_used ?? 0}
              </p>
              <p className="text-xs text-slate-400">{item.unit}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">Remaining</p>
              <p className="text-lg font-bold text-emerald-600">
                {item.total_remaining ?? Math.max(0, (item.total_assigned ?? 0) - (item.total_used ?? 0))}
              </p>
              <p className="text-xs text-slate-400">{item.unit}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Quantity to Add <span className="text-rose-500">*</span>
              <span className="ml-1 text-slate-400 font-normal">({item.unit})</span>
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); setError(''); }}
              placeholder={`Enter quantity in ${item.unit}`}
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-800 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all
                ${error ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
            />
            {error && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" /> {error}
              </p>
            )}
          </div>

          {/* After adding preview */}
          {quantity > 0 && !isNaN(parseInt(quantity)) && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-emerald-50 border border-emerald-100 text-sm">
              <TrendingUp className="h-4 w-4 text-emerald-600 shrink-0" />
              <span className="text-emerald-700">
                After adding:{' '}
                <span className="font-semibold">
                  {(item.total_assigned ?? 0) + parseInt(quantity)} {item.unit}
                </span>{' '}
                assigned,{' '}
                <span className="font-semibold">
                  {Math.max(0, (item.total_assigned ?? 0) + parseInt(quantity) - (item.total_used ?? 0))} {item.unit}
                </span>{' '}
                remaining
              </span>
            </div>
          )}

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Remarks <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder="e.g. Monthly restock, Emergency procurement..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white
                text-sm text-slate-800 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                transition-all resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700
                transition-colors shadow-sm shadow-emerald-200 disabled:opacity-60 flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              <PlusCircle className="h-4 w-4" />
              Add Stock
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Modal Form ───────────────────────────────────────────────────────────────

const InventoryModal = ({ open, mode, formData, onChange, onSubmit, onClose, loading, errors }) => {
  if (!open) return null;

  const isEdit = mode === 'edit';

  return (
    <div className="fixed inset-0 z-[990] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg animate-[fadeUp_0.2s_ease_forwards] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">
                {isEdit ? 'Edit Inventory Item' : 'Add New Inventory Item'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {isEdit ? 'Update item details below' : 'Fill in the details to create a new item'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Item Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="item_name"
              value={formData.item_name}
              onChange={onChange}
              placeholder="e.g. Surgical Gloves, ECG Machine"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-800 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all
                ${errors.item_name ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
            />
            {errors.item_name && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" /> {errors.item_name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Item Code <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="item_code"
              value={formData.item_code}
              onChange={onChange}
              placeholder="e.g. ECG-001"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-800 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all
                ${errors.item_code ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
            />
            {errors.item_code && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" /> {errors.item_code}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Category <span className="text-rose-500">*</span>
            </label>
            <div className="flex gap-3">
              {CATEGORY_OPTIONS.map((cat) => {
                const meta = CATEGORY_META[cat];
                const IconComp = meta.icon;
                const selected = formData.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => onChange({ target: { name: 'category', value: cat } })}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all
                      ${selected
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-200'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <IconComp className="h-4 w-4" />
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Unit <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="unit"
              value={formData.unit}
              onChange={onChange}
              placeholder="e.g. Box, Piece, Set, Pair"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-800 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all
                ${errors.unit ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
            />
            {errors.unit && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" /> {errors.unit}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
            <div className="flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50">
              {STATUS_OPTIONS.map((s) => {
                const meta = STATUS_META[s];
                const selected = formData.status === s;
                return (
                  <label key={s} className="flex items-center gap-2.5 cursor-pointer group">
                    <div
                      onClick={() => onChange({ target: { name: 'status', value: s } })}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                        ${selected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 group-hover:border-indigo-400'}`}
                    >
                      {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                      {s}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200 disabled:opacity-60 flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Update Item' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Skeleton Row ─────────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(8)].map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div className="h-4 bg-slate-100 rounded-full" style={{ width: `${60 + Math.random() * 30}%` }} />
      </td>
    ))}
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const InventoryMasterManagement = () => {
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── NEW: Add Stock modal state ────────────────────────────────────────────
  const [addStockModalOpen, setAddStockModalOpen] = useState(false);
  const [addStockTarget, setAddStockTarget] = useState(null);

  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);

  // ── Toast helpers ────────────────────────────────────────────────────────────

  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastCounter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Debounce search ───────────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch items ───────────────────────────────────────────────────────────────

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.is_active = statusFilter === 'ACTIVE' ? 'true' : 'false';

      const { data } = await api.get(`${API_BASE}${ENDPOINT}`, { params });
      setItems(data.results || []);
      setPagination({ count: data.count, next: data.next, previous: data.previous });
    } catch (err) {
      addToast('Failed to load inventory items. Please try again.', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, categoryFilter, statusFilter, addToast]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Form validation ───────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!formData.item_name.trim()) errs.item_name = 'Item name is required.';
    if (!formData.item_code.trim()) errs.item_code = 'Item code is required.';
    if (!formData.unit.trim()) errs.unit = 'Unit is required.';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── CRUD handlers ─────────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setEditId(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setFormData({
      item_name: item.item_name || '',
      item_code: item.item_code || '',
      category: item.category || 'CONSUMABLE',
      unit: item.unit || '',
      status: item.is_active === false ? 'INACTIVE' : (item.status || 'ACTIVE'),
    });
    setFormErrors({});
    setEditId(item.id);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setActionLoading(true);
    const payload = {
      item_name: formData.item_name.trim(),
      item_code: formData.item_code.trim(),
      category: formData.category,
      unit: formData.unit.trim(),
      status: formData.status,
      is_active: formData.status === 'ACTIVE',
    };

    try {
      if (modalMode === 'add') {
        await api.post(`${API_BASE}${ENDPOINT}`, payload);
        addToast('Inventory item created successfully.', 'success');
      } else {
        await api.put(`${API_BASE}${ENDPOINT}${editId}/`, payload);
        addToast('Inventory item updated successfully.', 'success');
      }
      setModalOpen(false);
      setCurrentPage(1);
      fetchItems();
    } catch (err) {
      const msg =
        err.response?.data?.item_name?.[0] ||
        err.response?.data?.detail ||
        'Something went wrong. Please try again.';
      addToast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setActionLoading(true);
    try {
      await api.delete(`${API_BASE}${ENDPOINT}${deleteTarget.id}/`);
      addToast(`"${deleteTarget.item_name}" has been deleted.`, 'success');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      if (items.length === 1 && currentPage > 1) setCurrentPage((p) => p - 1);
      else fetchItems();
    } catch (err) {
      addToast('Failed to delete item. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── NEW: Add Stock handlers ────────────────────────────────────────────────

  const handleOpenAddStock = (item) => {
    setAddStockTarget(item);
    setAddStockModalOpen(true);
  };

  const handleAddStockSuccess = (responseData) => {
    addToast(
      `Stock added! ${responseData.item.item_name}: ${responseData.item.total_remaining} ${responseData.item.unit} remaining`,
      'success'
    );
    setAddStockModalOpen(false);
    setAddStockTarget(null);
    // Refresh list to show updated totals
    fetchItems();
  };

  // ── Pagination ────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(pagination.count / PAGE_SIZE);

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const resetFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const hasFilters = search || categoryFilter || statusFilter;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Inject keyframe animations */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes fadeUp {
          from { transform: translateY(12px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <Toast toasts={toasts} removeToast={removeToast} />

      <ConfirmDialog
        open={deleteDialogOpen}
        item={deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
        loading={actionLoading}
      />

      <InventoryModal
        open={modalOpen}
        mode={modalMode}
        formData={formData}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
        loading={actionLoading}
        errors={formErrors}
      />

      {/* NEW: Add Stock Modal */}
      <AddStockModal
        open={addStockModalOpen}
        item={addStockTarget}
        onClose={() => { setAddStockModalOpen(false); setAddStockTarget(null); }}
        onSuccess={handleAddStockSuccess}
      />

      <div className="min-h-screen bg-slate-50 font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

          {/* ── Header ─────────────────────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-md shadow-indigo-200">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Inventory Master</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {loading ? 'Loading…' : `${pagination.count} item${pagination.count !== 1 ? 's' : ''} total`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={fetchItems}
                disabled={loading}
                className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 transition-colors shadow-sm disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleOpenAdd}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </button>
            </div>
          </div>

          {/* ── Stats Row ──────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Items', value: pagination.count, icon: Layers, color: 'indigo' },
              { label: 'Consumables', value: items.filter(i => i.category === 'CONSUMABLE').length, icon: Box, color: 'violet' },
              { label: 'Capital Items', value: items.filter(i => i.category === 'CAPITAL').length, icon: Archive, color: 'amber' },
              { label: 'Active', value: items.filter(i => i.is_active !== false && i.status !== 'INACTIVE').length, icon: CheckCircle, color: 'emerald' },
            ].map((stat) => {
              const IconComp = stat.icon;
              const colorMap = {
                indigo: 'bg-indigo-50 text-indigo-600',
                violet: 'bg-violet-50 text-violet-600',
                amber: 'bg-amber-50 text-amber-600',
                emerald: 'bg-emerald-50 text-emerald-600',
              };
              return (
                <div key={stat.label} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{stat.label}</span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[stat.color]}`}>
                      <IconComp className="h-4 w-4" />
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-slate-800">
                    {loading ? <span className="block w-10 h-6 bg-slate-100 rounded animate-pulse" /> : stat.value}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── Filters ────────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by item name, code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder-slate-400"
                />
              </div>

              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                  className="pl-9 pr-8 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 appearance-none cursor-pointer min-w-[150px]"
                >
                  <option value="">All Categories</option>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="pl-9 pr-8 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 appearance-none cursor-pointer min-w-[130px]"
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {hasFilters && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-rose-600 border border-slate-200 rounded-xl hover:border-rose-200 bg-white transition-colors whitespace-nowrap"
                >
                  <X className="h-4 w-4" /> Clear
                </button>
              )}
            </div>
          </div>

          {/* ── Table ──────────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    {['Item', 'Category', 'Unit', 'Assigned', 'Used', 'Remaining', 'Status', 'Actions'].map((col) => (
                      <th
                        key={col}
                        className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading
                    ? [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
                    : items.length === 0
                    ? (
                      <tr>
                        <td colSpan={8} className="py-20 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                              <Package className="h-7 w-7 text-slate-300" />
                            </div>
                            <p className="text-slate-500 font-medium">No inventory items found</p>
                            <p className="text-slate-400 text-xs">
                              {hasFilters ? 'Try adjusting your filters' : 'Start by adding your first item'}
                            </p>
                            {!hasFilters && (
                              <button
                                onClick={handleOpenAdd}
                                className="mt-1 flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                              >
                                <Plus className="h-4 w-4" /> Add Item
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                    : items.map((item) => {
                      const catMeta = CATEGORY_META[item.category] || CATEGORY_META.CONSUMABLE;
                      const CatIcon = catMeta.icon;
                      const isActive = item.is_active !== false && item.status !== 'INACTIVE';
                      const statusKey = isActive ? 'ACTIVE' : 'INACTIVE';
                      const statMeta = STATUS_META[statusKey];

                      const totalAssigned = item.total_assigned ?? 0;
                      const totalUsed = item.total_used ?? 0;
                      const totalRemaining = item.total_remaining ?? Math.max(0, totalAssigned - totalUsed);

                      // Colour-code remaining stock
                      const remainingColor = totalRemaining === 0
                        ? 'text-rose-600 font-semibold'
                        : totalRemaining < 10
                        ? 'text-amber-600 font-semibold'
                        : 'text-emerald-600 font-semibold';

                      return (
                        <tr key={item.id} className="hover:bg-slate-50/60 transition-colors group">

                          {/* Item */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                <ClipboardList className="h-4 w-4 text-indigo-500" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{item.item_name}</p>
                                <p className="text-xs text-slate-400">{item.item_code}</p>
                              </div>
                            </div>
                          </td>

                          {/* Category */}
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${catMeta.color}`}>
                              <CatIcon className="h-3 w-3" />
                              {item.category}
                            </span>
                          </td>

                          {/* Unit */}
                          <td className="px-5 py-4 text-slate-600">{item.unit || '—'}</td>

                          {/* Assigned */}
                          <td className="px-5 py-4">
                            <span className="text-slate-700 font-medium">{totalAssigned}</span>
                            <span className="text-slate-400 text-xs ml-1">{item.unit}</span>
                          </td>

                          {/* Used */}
                          <td className="px-5 py-4">
                            <span className="text-slate-600">{totalUsed}</span>
                            <span className="text-slate-400 text-xs ml-1">{item.unit}</span>
                          </td>

                          {/* Remaining */}
                          <td className="px-5 py-4">
                            <span className={remainingColor}>{totalRemaining}</span>
                            <span className="text-slate-400 text-xs ml-1">{item.unit}</span>
                          </td>

                          {/* Status */}
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${statMeta.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${statMeta.dot}`} />
                              {statusKey}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Add Stock */}
                              <button
                                onClick={() => handleOpenAddStock(item)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-xs font-medium transition-colors"
                                title="Add Stock"
                              >
                                <PlusCircle className="h-3.5 w-3.5" />
                                Add Stock
                              </button>
                              {/* Edit */}
                              <button
                                onClick={() => handleOpenEdit(item)}
                                className="p-1.5 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteClick(item)}
                                className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-slate-100">
              {loading
                ? [...Array(4)].map((_, i) => (
                  <div key={i} className="p-4 animate-pulse space-y-2">
                    <div className="h-4 bg-slate-100 rounded w-2/3" />
                    <div className="h-3 bg-slate-100 rounded w-1/3" />
                  </div>
                ))
                : items.length === 0
                ? (
                  <div className="py-16 text-center">
                    <Package className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">No items found</p>
                  </div>
                )
                : items.map((item) => {
                  const catMeta = CATEGORY_META[item.category] || CATEGORY_META.CONSUMABLE;
                  const CatIcon = catMeta.icon;
                  const isActive = item.is_active !== false && item.status !== 'INACTIVE';
                  const statusKey = isActive ? 'ACTIVE' : 'INACTIVE';
                  const statMeta = STATUS_META[statusKey];
                  const totalAssigned = item.total_assigned ?? 0;
                  const totalUsed = item.total_used ?? 0;
                  const totalRemaining = item.total_remaining ?? Math.max(0, totalAssigned - totalUsed);

                  return (
                    <div key={item.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                            <ClipboardList className="h-4 w-4 text-indigo-500" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 text-sm truncate">{item.item_name}</p>
                            <p className="text-xs text-slate-400">{item.item_code}</p>

                            {/* Stock mini-grid */}
                            <div className="grid grid-cols-3 gap-2 mt-2 p-2 rounded-lg bg-slate-50 border border-slate-100">
                              <div className="text-center">
                                <p className="text-xs text-slate-400">Assigned</p>
                                <p className="text-sm font-bold text-slate-700">{totalAssigned}</p>
                              </div>
                              <div className="text-center border-x border-slate-200">
                                <p className="text-xs text-slate-400">Used</p>
                                <p className="text-sm font-bold text-rose-500">{totalUsed}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-slate-400">Remaining</p>
                                <p className={`text-sm font-bold ${totalRemaining === 0 ? 'text-rose-600' : totalRemaining < 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {totalRemaining}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${catMeta.color}`}>
                                <CatIcon className="h-3 w-3" />
                                {item.category}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${statMeta.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statMeta.dot}`} />
                                {statusKey}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {/* Add Stock button (mobile) */}
                          <button
                            onClick={() => handleOpenAddStock(item)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-medium"
                          >
                            <PlusCircle className="h-3.5 w-3.5" />
                            Stock
                          </button>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleOpenEdit(item)}
                              className="p-2 rounded-xl hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(item)}
                              className="p-2 rounded-xl hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* ── Pagination Footer ───────────────────────────────────────────── */}
            {!loading && items.length > 0 && totalPages > 1 && (
              <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  Showing{' '}
                  <span className="font-semibold text-slate-700">
                    {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, pagination.count)}
                  </span>{' '}
                  of <span className="font-semibold text-slate-700">{pagination.count}</span> items
                </p>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.previous}
                    className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {getPageNumbers().map((pg, idx) =>
                    pg === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-1.5 text-slate-400 text-sm">…</span>
                    ) : (
                      <button
                        key={pg}
                        onClick={() => setCurrentPage(pg)}
                        className={`min-w-[36px] h-9 rounded-xl text-sm font-medium transition-colors border
                          ${currentPage === pg
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-200'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
                          }`}
                      >
                        {pg}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={!pagination.next}
                    className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InventoryMasterManagement;